import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Tag, ArrowRight, BookOpen } from "lucide-react";
import { useSEO, addJsonLd } from "../hooks/useSEO";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function BlogListPage() {
  const [posts, setPosts] = useState([]);
  const navigate = useNavigate();

  useSEO({ title: "Visa Blog & Guides | FlyInVisa", description: "Expert visa guides, travel tips, and application advice. Learn about visa requirements, processing times, and tips for approval.", keywords: ["visa blog", "travel guides", "visa tips", "visa application guide"] });

  useEffect(() => { axios.get(`${API}/blog`).then(r => setPosts(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    addJsonLd({ "@context": "https://schema.org", "@type": "Blog", "name": "FlyInVisa Blog", "description": "Expert visa guides and travel tips", "publisher": { "@type": "Organization", "name": "FlyInVisa" } });
  }, []);

  return (
    <div className="min-h-screen px-6 py-8 pt-24 bg-slate-50">
      <div className="max-w-4xl mx-auto">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 text-sm text-slate-400 hover:text-blue-600 mb-6"><ArrowLeft className="w-4 h-4" /> Home</button>
        <div className="text-center mb-12">
          <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4 border border-blue-100"><BookOpen className="w-7 h-7 text-blue-600" /></div>
          <h1 className="heading-font text-3xl font-bold text-slate-900 mb-2">Visa Guides & Blog</h1>
          <p className="text-sm text-slate-500">Expert advice on visa applications, travel tips, and country guides</p>
        </div>

        <div className="space-y-5">
          {posts.map((post, i) => (
            <motion.article key={post.slug} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="card-elevated p-6 cursor-pointer group" onClick={() => navigate(`/blog/${post.slug}`)} data-testid={`blog-${post.slug}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">{post.category}</span>
                    <span className="text-[10px] text-slate-400 flex items-center gap-1"><Calendar className="w-3 h-3" />{post.date}</span>
                  </div>
                  <h2 className="heading-font text-lg font-semibold text-slate-900 group-hover:text-blue-600 transition-colors mb-1">{post.title}</h2>
                  <p className="text-sm text-slate-500 line-clamp-2">{post.description}</p>
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {post.keywords?.slice(0, 3).map(k => <span key={k} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500"><Tag className="w-2.5 h-2.5 inline mr-0.5" />{k}</span>)}
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-blue-500 shrink-0 mt-2" />
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </div>
  );
}

export function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);

  useEffect(() => { axios.get(`${API}/blog/${slug}`).then(r => setPost(r.data)).catch(() => navigate("/blog")); }, [slug]);

  useEffect(() => {
    if (post) {
      addJsonLd({ "@context": "https://schema.org", "@type": "Article", "headline": post.title, "description": post.description, "datePublished": post.date, "author": { "@type": "Organization", "name": "FlyInVisa" }, "publisher": { "@type": "Organization", "name": "FlyInVisa" } });
    }
  }, [post]);

  useSEO({ title: post?.title ? `${post.title} | FlyInVisa Blog` : "Loading...", description: post?.description || "", keywords: post?.keywords || [] });

  if (!post) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen px-6 py-8 pt-24 bg-slate-50">
      <article className="max-w-3xl mx-auto">
        <button onClick={() => navigate("/blog")} className="flex items-center gap-2 text-sm text-slate-400 hover:text-blue-600 mb-6"><ArrowLeft className="w-4 h-4" /> All Posts</button>

        <div className="mb-8">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2.5 py-1 rounded-full">{post.category}</span>
            <span className="text-xs text-slate-400">{post.date}</span>
          </div>
          <h1 className="heading-font text-3xl md:text-4xl font-bold text-slate-900 mb-4">{post.title}</h1>
          <p className="text-base text-slate-500">{post.description}</p>
        </div>

        <div className="card-elevated rounded-2xl p-8 prose prose-slate prose-sm max-w-none">
          {post.content.split("\n").map((line, i) => {
            if (line.startsWith("## ")) return <h2 key={i} className="heading-font text-xl font-bold text-slate-900 mt-6 mb-3">{line.replace("## ", "")}</h2>;
            if (line.startsWith("- **")) { const m = line.match(/- \*\*(.+?)\*\*:?\s*(.*)/); return m ? <div key={i} className="flex items-start gap-2 mb-1.5"><div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 shrink-0" /><span className="text-sm text-slate-700"><strong>{m[1]}</strong>{m[2] ? `: ${m[2]}` : ""}</span></div> : <p key={i} className="text-sm text-slate-600 mb-1">{line}</p>; }
            if (line.match(/^\d+\./)) return <div key={i} className="flex items-start gap-2 mb-1.5 ml-2"><span className="text-sm text-slate-700">{line}</span></div>;
            if (line.trim() === "") return <div key={i} className="h-3" />;
            return <p key={i} className="text-sm text-slate-600 leading-relaxed mb-2">{line}</p>;
          })}
        </div>

        {/* CTA */}
        <div className="card-elevated rounded-2xl p-6 mt-8 text-center bg-blue-50 border-blue-100">
          <h3 className="heading-font text-lg font-bold text-slate-900 mb-2">Ready to Apply?</h3>
          <p className="text-sm text-slate-500 mb-4">Start your visa application today with transparent pricing and fast processing.</p>
          <button onClick={() => navigate("/pricing")} className="btn-premium text-white font-semibold px-6 py-3 rounded-xl inline-flex items-center gap-2">
            Check Visa Pricing <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Keywords */}
        <div className="mt-6 flex flex-wrap gap-2">
          {post.keywords.map(k => <span key={k} className="text-xs px-3 py-1 rounded-full bg-slate-100 text-slate-500">{k}</span>)}
        </div>
      </article>
    </div>
  );
}
